import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Lightning, Funnel, ShieldCheck, TrendUp, CheckCircle, ChatText, Prohibit, DownloadSimple, Warning, CircleNotch } from "@phosphor-icons/react";

const Card = ({ title, icon: Icon, count, children, testid }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><Icon size={15} /> {title}</h3>
      {count != null && <span className="font-mono text-xs text-zinc-500">{count}</span>}
    </div>
    <div className="max-h-72 overflow-y-auto space-y-2">{children}</div>
  </div>
);

function OverrideDialog({ item, onOpenChange, onDone }) {
  const [note, setNote] = useState("");
  const override = useMutation({
    mutationFn: () => api.post("/flightdeck/override", { kind: item.kind === "document" ? "document" : "8867", ref_id: item.ref_id, justification: note }),
    onSuccess: () => { toast.success("Flag overridden — justification recorded in the audit log"); onDone(); onOpenChange(false); },
    onError: (e) => toast.error(errText(e)),
  });
  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading text-sm">Override AI flag — justification required</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500">{item?.issue || item?.detail}</p>
        <Input data-testid="override-justification-input" placeholder="Internal justification note (10+ characters)…" value={note} onChange={(e) => setNote(e.target.value)} />
        <Button data-testid="override-confirm-btn" disabled={note.trim().length < 10 || override.isPending} onClick={() => override.mutate()}>Record override</Button>
      </DialogContent>
    </Dialog>
  );
}

export function ActionQueue({ q }) {
  const qc = useQueryClient();
  const [overrideItem, setOverrideItem] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["flightdeck"] });
  const approve = useMutation({
    mutationFn: (id) => api.post("/flightdeck/approve-match", { document_id: id }),
    onSuccess: () => { toast.success("Match approved — AI routing confirmed"); refresh(); },
    onError: (e) => toast.error(errText(e)),
  });
  const nudge = useMutation({
    mutationFn: (t) => api.post("/flightdeck/nudge", { client_id: t.client_id, issue: t.issue }),
    onSuccess: () => toast.success("Fix-it nudge sent to the client's portal"),
    onError: (e) => toast.error(errText(e)),
  });
  const exportPkg = async (engId, name) => {
    const r = await api.get(`/flightdeck/export/${engId}?format=json`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `return_${name.replace(/ /g, "_")}.json`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Return schema exported for tax software import");
  };
  const total = (q?.triage?.length || 0) + (q?.review_ready?.length || 0) + (q?.blocked?.length || 0);
  return (
    <Card title="Daily action queue" icon={Lightning} count={total} testid="quadrant-action-queue">
      {(q?.triage || []).map((t) => (
        <div key={t.document_id} data-testid={`triage-${t.document_id}`} className="rounded-md border border-rose-200 bg-rose-50/50 p-2.5 group">
          <p className="text-xs text-zinc-800"><span className="text-[9px] uppercase bg-rose-100 text-rose-700 rounded-sm px-1 py-0.5 mr-1.5">Triage</span>{t.client_name} · <span className="font-mono">{t.filename}</span></p>
          <p className="text-[10px] text-zinc-600 mt-0.5">{t.issue}</p>
          <div className="flex gap-3 mt-1.5">
            <button data-testid={`approve-match-${t.document_id}`} disabled={approve.isPending} onClick={() => approve.mutate(t.document_id)} className="flex items-center gap-0.5 text-[10px] text-emerald-700 underline hover:no-underline"><CheckCircle size={11} /> Approve match</button>
            <button data-testid={`nudge-${t.document_id}`} disabled={nudge.isPending} onClick={() => nudge.mutate(t)} className="flex items-center gap-0.5 text-[10px] text-zinc-700 underline hover:no-underline"><ChatText size={11} /> Draft request nudge</button>
            <button data-testid={`override-${t.document_id}`} onClick={() => setOverrideItem({ kind: "document", ref_id: t.document_id, issue: t.issue })} className="flex items-center gap-0.5 text-[10px] text-zinc-500 underline hover:no-underline"><Prohibit size={11} /> Override flag</button>
          </div>
        </div>
      ))}
      {(q?.review_ready || []).map((r) => (
        <div key={r.engagement_id} data-testid={`review-ready-${r.engagement_id}`} className="rounded-md border border-emerald-200 bg-emerald-50/40 p-2.5 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs text-zinc-800"><span className="text-[9px] uppercase bg-emerald-100 text-emerald-700 rounded-sm px-1 py-0.5 mr-1.5">Review ready</span><Link to={`/clients/${r.client_id}`} className="hover:underline">{r.client_name}</Link></p>
            <p className="text-[10px] text-zinc-500 mt-0.5">{r.service_type} · {r.tax_year} — gaps cleared, due diligence verified</p>
          </div>
          <button data-testid={`export-${r.engagement_id}`} title="Export return schema (JSON) to tax software" onClick={() => exportPkg(r.engagement_id, r.client_name)} className="shrink-0 text-zinc-400 hover:text-zinc-900 transition-colors duration-150"><DownloadSimple size={15} /></button>
        </div>
      ))}
      {(q?.blocked || []).map((b) => (
        <div key={b.engagement_id} data-testid={`blocked-${b.engagement_id}`} className="rounded-md border border-zinc-100 p-2.5">
          <p className="text-xs text-zinc-700"><span className="text-[9px] uppercase bg-zinc-100 text-zinc-500 rounded-sm px-1 py-0.5 mr-1.5">Awaiting client</span><Link to={`/clients/${b.client_id}`} className="hover:underline">{b.client_name}</Link></p>
          <p className="text-[10px] text-zinc-400 mt-0.5">Waiting on: {b.waiting_on.join(" · ")}</p>
        </div>
      ))}
      {total === 0 && <p className="text-xs text-zinc-400 italic">Inbox zero — nothing needs your attention.</p>}
      {overrideItem && <OverrideDialog item={overrideItem} onOpenChange={(o) => !o && setOverrideItem(null)} onDone={refresh} />}
    </Card>
  );
}

export function FunnelCard({ f }) {
  const rows = [["Ingesting", f?.ingesting], ["AI interview open", f?.ai_interview], ["CPA review", f?.cpa_review], ["Ready to file", f?.ready_to_file]];
  const max = Math.max(...rows.map(([, v]) => v || 0), 1);
  return (
    <Card title="The funnel — portfolio status" icon={Funnel} count={`${f?.total ?? 0} active`} testid="quadrant-funnel">
      {rows.map(([label, v]) => (
        <div key={label}>
          <div className="flex justify-between text-xs mb-1"><span className="text-zinc-700">{label}</span><span className="font-mono text-zinc-600">{v ?? 0}</span></div>
          <div className="h-2.5 bg-zinc-100 rounded-sm overflow-hidden"><div className="h-full bg-zinc-900 rounded-sm" style={{ width: `${Math.round(((v || 0) / max) * 100)}%` }} /></div>
        </div>
      ))}
    </Card>
  );
}

const SHIELD_STYLE = { blocked: "text-rose-700 bg-rose-50 border-rose-200", missing: "text-rose-700 bg-rose-50 border-rose-200", open: "text-amber-700 bg-amber-50 border-amber-200", verified: "text-emerald-700 bg-emerald-50 border-emerald-200", confirmed: "text-emerald-700 bg-emerald-50 border-emerald-200", disclosed: "text-emerald-700 bg-emerald-50 border-emerald-200", overridden: "text-zinc-500 bg-zinc-100 border-zinc-200" };

export function ComplianceShield({ s }) {
  const [overrideItem, setOverrideItem] = useState(null);
  const qc = useQueryClient();
  const sections = [["Form 8867 readiness radar", s?.form_8867], ["IRC § 274(d) substantiation", s?.substantiation_274d], ["Cryptocurrency disclosures", s?.crypto]];
  const issues = sections.reduce((n, [, arr]) => n + (arr || []).filter((x) => ["blocked", "open", "missing"].includes(x.status)).length, 0);
  return (
    <Card title="Compliance shield" icon={ShieldCheck} count={`${issues} risk${issues === 1 ? "" : "s"}`} testid="quadrant-compliance-shield">
      {sections.map(([label, items]) => (
        <div key={label}>
          <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">{label}</p>
          {(items || []).map((x) => (
            <div key={`${label}-${x.engagement_id}`} data-testid={`shield-${x.status}-${x.engagement_id}`} className="flex items-start justify-between gap-2 py-1">
              <div className="min-w-0">
                <p className="text-xs text-zinc-800 flex items-center gap-1">{x.status === "blocked" && <Warning size={12} weight="fill" className="text-rose-600 shrink-0" />}{x.client_name}</p>
                <p className={`text-[10px] mt-0.5 ${x.status === "blocked" ? "text-rose-700 font-medium" : "text-zinc-500"}`}>{x.detail}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className={`text-[9px] uppercase border rounded-sm px-1.5 py-0.5 ${SHIELD_STYLE[x.status] || SHIELD_STYLE.open}`}>{x.status}</span>
                {x.status === "blocked" && (
                  <button data-testid={`shield-override-${x.engagement_id}`} title="Override with justification" onClick={() => setOverrideItem({ kind: "8867", ref_id: x.engagement_id, detail: x.detail })} className="text-[9px] text-zinc-500 underline hover:no-underline">override</button>
                )}
              </div>
            </div>
          ))}
          {(items || []).length === 0 && <p className="text-[10px] text-zinc-400 italic mb-1">Nothing tracked yet.</p>}
        </div>
      ))}
      {overrideItem && <OverrideDialog item={overrideItem} onOpenChange={(o) => !o && setOverrideItem(null)} onDone={() => qc.invalidateQueries({ queryKey: ["flightdeck"] })} />}
    </Card>
  );
}

export function OpportunityEngine({ opportunities }) {
  const qc = useQueryClient();
  const [pending, setPending] = useState(null);
  const act = useMutation({
    mutationFn: (o) => api.post("/flightdeck/opportunity-action", { key: o.key, client_id: o.client_id }),
    onSuccess: ({ data }) => { toast.success(data.message); setPending(null); qc.invalidateQueries({ queryKey: ["flightdeck"] }); },
    onError: (e) => { toast.error(errText(e)); setPending(null); },
  });
  return (
    <Card title="Opportunity & advisory engine" icon={TrendUp} count={(opportunities || []).length} testid="quadrant-opportunities">
      {(opportunities || []).map((o) => (
        <div key={`${o.client_id}-${o.key}`} data-testid={`opp-${o.key}-${o.client_id}`} className="rounded-md border border-indigo-100 bg-indigo-50/40 p-2.5">
          <p className="text-xs font-medium text-zinc-900">{o.title} <span className="text-zinc-400 font-normal">· {o.client_name}</span></p>
          <p className="text-[10px] text-zinc-600 mt-0.5">{o.trigger}</p>
          {o.acted ? (
            <p className="text-[10px] text-emerald-700 mt-1.5 flex items-center gap-1"><CheckCircle size={11} weight="fill" /> Action triggered</p>
          ) : (
            <Button data-testid={`opp-action-${o.key}-${o.client_id}`} size="sm" variant="outline" className="h-6 text-[10px] mt-1.5"
              disabled={act.isPending && pending === `${o.client_id}-${o.key}`}
              onClick={() => { setPending(`${o.client_id}-${o.key}`); act.mutate(o); }}>
              {act.isPending && pending === `${o.client_id}-${o.key}` ? <CircleNotch size={11} className="animate-spin mr-1" /> : null}
              {o.action_label}
            </Button>
          )}
        </div>
      ))}
      {(opportunities || []).length === 0 && <p className="text-xs text-zinc-400 italic">No planning opportunities surfaced yet — import prior-year returns to feed the engine.</p>}
    </Card>
  );
}
